// src/contexts/SettingsContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(() => {
        const saved = localStorage.getItem('app_settings');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                // If parsing fails, use defaults
            }
        }
        return {
            language: 'pt-BR',
            theme: 'light',
        };
    });

    useEffect(() => {
        // Apply theme
        const theme = settings.theme || 'light';

        // Remove existing theme classes
        document.documentElement.classList.remove('theme-light', 'theme-dark');
        document.documentElement.removeAttribute('data-theme');

        if (theme === 'dark') {
            document.documentElement.classList.add('theme-dark');
            document.documentElement.setAttribute('data-theme', 'dark');
            // Import dark theme variables dynamically
            import('./../styles/dark-variables.css');
        } else {
            document.documentElement.classList.add('theme-light');
            document.documentElement.setAttribute('data-theme', 'light');
            // Light is default, variables.css is already imported in App.js
        }

        // Apply background color to body
        document.body.style.backgroundColor = theme === 'dark' ? '#1a1a2e' : '#f5f7fa';
        document.body.style.color = theme === 'dark' ? '#e4e4e4' : '#343a40';

        // Save to localStorage
        localStorage.setItem('app_settings', JSON.stringify(settings));
    }, [settings]);

    const updateSettings = (newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    };

    const value = {
        settings,
        updateSettings,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};